import { DeleteOutlined, PrinterOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, App, Button, Card, Flex, Input, InputNumber, Radio, Segmented, Select, Space, Table, Typography } from "antd";
import { useDeferredValue, useState } from "react";
import { useSearchParams } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { CLIENT_PAGING } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { SkuSelect } from "@/pages/catalog/mapping";
import { useWmsWarehouse } from "@/pages/warehouse/common";
import { printPdf } from "@/pages/warehouse/packLabel";
import { useZones } from "@/pages/warehouse/Zones";

type Kind = "location" | "tote" | "pallet" | "packaging" | "sku" | "custom";
type Symbology = "code128" | "qr" | "ean13";
type Item = { id?: string; text?: string; title?: string; copies?: number };
type SkuRow = Pick<Schemas["SKU"], "id" | "code" | "name" | "barcode"> & { copies: number };

const WAREHOUSE_KINDS: Kind[] = ["location", "tote", "pallet", "packaging"];
const SIZES = ["33x15", "40x30", "50x30", "50x25", "60x40", "100x50", "100x100", "100x150"];
const SETUP_KEY = "zerpid.barcodes.setup";

type Setup = { symbology: Symbology; size: string; width: number; height: number; sheet: boolean };
const DEFAULT_SETUP: Setup = { symbology: "code128", size: "40x30", width: 40, height: 30, sheet: false };

function loadSetup(): Setup {
  try {
    const raw = localStorage.getItem(SETUP_KEY);
    return raw ? { ...DEFAULT_SETUP, ...(JSON.parse(raw) as Partial<Setup>) } : DEFAULT_SETUP;
  } catch {
    return DEFAULT_SETUP;
  }
}

function saveSetup(s: Setup) {
  try {
    localStorage.setItem(SETUP_KEY, JSON.stringify(s));
  } catch {
    // storage blocked: the choice lasts until reload
  }
}

/**
 * Alat > Cetak barcode: stickers of anything the floor scans (locations,
 * totes and carts, pallets, packaging, SKUs) or of free text, as Code 128, QR
 * or EAN-13, at the size of the labels the seller already has.
 */
export function Barcodes() {
  const { t } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const [params] = useSearchParams();
  const floor = can("warehouse.operate");
  const { current } = useWmsWarehouse();
  const kinds: Kind[] = [...(floor ? WAREHOUSE_KINDS : []), ...(can("catalog.view", "warehouse.operate") ? (["sku"] as Kind[]) : []), "custom"];
  const asked = params.get("kind") as Kind | null;
  const [kind, setKind] = useState<Kind>(asked && kinds.includes(asked) ? asked : (kinds[0] ?? "custom"));
  const [setup, setSetupState] = useState<Setup>(loadSetup);
  const setSetup = (patch: Partial<Setup>) =>
    setSetupState((s) => {
      const next = { ...s, ...patch };
      saveSetup(next);
      return next;
    });
  const [copies, setCopies] = useState(1);
  const [printing, setPrinting] = useState(false);

  const print = async (body: { items: Item[]; zone?: string; q?: string }) => {
    setPrinting(true);
    try {
      const request: Schemas["PrintBarcodes"] = {
        kind: kind === "tote" || kind === "pallet" ? "container" : kind,
        ...(WAREHOUSE_KINDS.includes(kind) && current ? { warehouseId: current.id } : {}),
        ...body,
        symbology: setup.symbology,
        width: setup.width,
        height: setup.height,
        sheet: setup.sheet,
      };
      printPdf(await unwrap(api.POST("/v1/barcodes/print", { body: request, parseAs: "blob" })));
    } catch (err) {
      void message.error(errorText(err));
    } finally {
      setPrinting(false);
    }
  };
  const withCopies = (ids: string[]): Item[] => ids.map((id) => ({ id, copies }));

  return (
    <>
      <Typography.Title level={2} style={{ margin: "0 0 16px", fontSize: 28 }}>
        {t("bc.title")}
      </Typography.Title>
      <Flex gap={16} wrap align="start">
        <Card style={{ flex: "2 1 560px", minWidth: 0 }}>
          <Segmented<Kind> value={kind} onChange={setKind} options={kinds.map((k) => ({ value: k, label: t(`bc.kind.${k}`) }))} style={{ marginBottom: 16, maxWidth: "100%", overflowX: "auto" }} />
          {WAREHOUSE_KINDS.includes(kind) && !current ? (
            <Alert type="info" showIcon title={t("bc.needWms")} />
          ) : kind === "location" ? (
            <LocationPicker warehouseId={current!.id} printing={printing} onPrint={(items, zone, q) => void print({ items: withCopies(items), zone, q })} />
          ) : kind === "tote" || kind === "pallet" || kind === "packaging" ? (
            <ListPicker kind={kind} warehouseId={current!.id} printing={printing} onPrint={(ids) => void print({ items: withCopies(ids) })} />
          ) : kind === "sku" ? (
            <SkuPicker printing={printing} onPrint={(items) => void print({ items })} />
          ) : (
            <CustomText printing={printing} copies={copies} onPrint={(items) => void print({ items })} />
          )}
        </Card>

        <Card title={t("bc.setup")} style={{ flex: "1 1 300px" }}>
          <Space orientation="vertical" size={16} style={{ width: "100%" }}>
            <div>
              <Typography.Text type="secondary">{t("bc.symbology")}</Typography.Text>
              <Segmented<Symbology>
                block
                value={setup.symbology}
                onChange={(symbology) => setSetup({ symbology })}
                options={[
                  { value: "code128", label: "Code 128" },
                  { value: "qr", label: "QR" },
                  { value: "ean13", label: "EAN-13" },
                ]}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t(`bc.symHelp.${setup.symbology}`)}
              </Typography.Text>
            </div>
            <div>
              <Typography.Text type="secondary">{t("bc.size")}</Typography.Text>
              <Select<string>
                value={setup.size}
                style={{ width: "100%" }}
                onChange={(size) => {
                  if (size === "custom") return setSetup({ size });
                  const [width, height] = size.split("x").map(Number);
                  setSetup({ size, width, height });
                }}
                options={[...SIZES.map((s) => ({ value: s, label: `${s.replace("x", " × ")} mm` })), { value: "custom", label: t("bc.sizeCustom") }]}
              />
              {setup.size === "custom" && (
                <Space style={{ marginTop: 8 }}>
                  <InputNumber min={15} max={200} value={setup.width} onChange={(v) => v && setSetup({ width: v })} suffix="mm" style={{ width: 110 }} />
                  ×
                  <InputNumber min={10} max={287} value={setup.height} onChange={(v) => v && setSetup({ height: v })} suffix="mm" style={{ width: 110 }} />
                </Space>
              )}
            </div>
            <div>
              <Typography.Text type="secondary">{t("bc.paper")}</Typography.Text>
              <Radio.Group value={setup.sheet} onChange={(e) => setSetup({ sheet: e.target.value as boolean })} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <Radio value={false}>{t("bc.paperRoll")}</Radio>
                <Radio value={true}>{t("bc.paperA4")}</Radio>
              </Radio.Group>
            </div>
            {kind !== "sku" && (
              <div>
                <Typography.Text type="secondary">{t("bc.copies")}</Typography.Text>
                <br />
                <InputNumber min={1} max={100} precision={0} value={copies} onChange={(v) => setCopies(v ?? 1)} style={{ width: 120 }} />
              </div>
            )}
            <Alert type="warning" showIcon title={t("bc.printTip")} description={t(setup.sheet ? "bc.printTipA4" : "bc.printTipRoll", { w: setup.width, h: setup.height })} />
          </Space>
        </Card>
      </Flex>
    </>
  );
}

function PrintButton({ n, printing, onClick, label }: { n: number; printing: boolean; onClick: () => void; label?: string }) {
  const { t } = useI18n();
  return (
    <Button type="primary" icon={<PrinterOutlined />} disabled={n === 0} loading={printing} onClick={onClick}>
      {label ?? t("bc.print", { n })}
    </Button>
  );
}

/** Locations: pick some, or print a whole zone or search result at once. */
function LocationPicker({ warehouseId, printing, onPrint }: { warehouseId: string; printing: boolean; onPrint: (ids: string[], zone?: string, q?: string) => void }) {
  const { t } = useI18n();
  const [search, setSearch] = useState("");
  const q = useDeferredValue(search.trim());
  const [zone, setZone] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const zones = useZones(warehouseId);
  const list = useQuery({
    queryKey: ["locations", warehouseId, q, zone],
    queryFn: () =>
      unwrap(
        api.GET("/v1/warehouses/{warehouseID}/locations", {
          params: { path: { warehouseID: warehouseId }, query: { status: "active", ...(q ? { q } : {}), ...(zone ? { zone: zone === "-" ? "" : zone } : {}) } },
        }),
      ),
  });
  const shown = list.data?.items.length ?? 0;
  // The list stops at 200; printing takes every match (up to 2000).
  const total = list.data?.nextOffset ? `${shown}+` : shown;
  return (
    <>
      <Flex gap={12} wrap style={{ marginBottom: 12 }}>
        <Input.Search allowClear placeholder={t("wms.loc.search")} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 220 }} />
        <Select<string>
          value={zone}
          onChange={setZone}
          style={{ width: 180 }}
          options={[
            { value: "", label: t("wms.loc.allZones") },
            ...(zones.data?.items ?? []).map((z) => ({ value: z.code, label: t("wms.zone.tag", { code: z.code }) })),
            { value: "-", label: t("wms.zone.none") },
          ]}
        />
        <PrintButton n={selected.length} printing={printing} onClick={() => onPrint(selected)} />
        <Button disabled={shown === 0} loading={printing} onClick={() => onPrint([], zone ? (zone === "-" ? "" : zone) : undefined, q || undefined)}>
          {t("bc.printAll", { n: total })}
        </Button>
      </Flex>
      <Table
        rowKey="id"
        size="small"
        loading={list.isPending}
        dataSource={list.data?.items ?? []}
        pagination={CLIENT_PAGING}
        rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys.map(String)) }}
        columns={[
          { title: t("wms.loc.code"), dataIndex: "code", render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
          { title: t("wms.loc.zone"), dataIndex: "zone", width: 90, render: (v: string) => v || "—" },
        ]}
      />
    </>
  );
}

/** Totes and carts, pallets, or packaging of the warehouse. */
function ListPicker({ kind, warehouseId, printing, onPrint }: { kind: "tote" | "pallet" | "packaging"; warehouseId: string; printing: boolean; onPrint: (ids: string[]) => void }) {
  const { t } = useI18n();
  const [selected, setSelected] = useState<string[]>([]);
  const list = useQuery({
    queryKey: ["barcodes", kind, warehouseId],
    queryFn: async (): Promise<{ id: string; code: string; note: string }[]> => {
      if (kind === "packaging") {
        const r = await unwrap(api.GET("/v1/warehouses/{warehouseID}/packagings", { params: { path: { warehouseID: warehouseId } } }));
        return r.items.filter((p) => p.active).map((p) => ({ id: p.id, code: p.code, note: p.name }));
      }
      const r = await unwrap(
        api.GET("/v1/warehouses/{warehouseID}/containers", { params: { path: { warehouseID: warehouseId }, query: kind === "pallet" ? { kind: "pallet" } : {} } }),
      );
      return r.items.map((c) => ({ id: c.id, code: c.code, note: c.kind === "cart" ? t("bc.cartSlots", { n: c.slots }) : "" }));
    },
  });
  return (
    <>
      <Flex gap={12} style={{ marginBottom: 12 }}>
        <PrintButton n={selected.length} printing={printing} onClick={() => onPrint(selected)} />
      </Flex>
      <Table
        rowKey="id"
        size="small"
        loading={list.isPending}
        dataSource={list.data ?? []}
        pagination={CLIENT_PAGING}
        rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys.map(String)) }}
        columns={[
          { title: t("wms.ct.code"), dataIndex: "code", width: 180, render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
          { dataIndex: "note" },
        ]}
      />
    </>
  );
}

/** SKUs with a count each; those without a barcode can get one made. */
function SkuPicker({ printing, onPrint }: { printing: boolean; onPrint: (items: Item[]) => void }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const [rows, setRows] = useState<SkuRow[]>([]);
  const add = async (id: string) => {
    if (rows.some((r) => r.id === id)) return;
    try {
      const s = await unwrap(api.GET("/v1/skus/{skuID}", { params: { path: { skuID: id } } }));
      setRows((rs) => [...rs, { id: s.id, code: s.code, name: s.name, barcode: s.barcode, copies: 1 }]);
    } catch (err) {
      void message.error(errorText(err));
    }
  };
  const missing = rows.filter((r) => !r.barcode);
  const generate = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/skus/barcodes/generate", { body: { ids: missing.map((r) => r.id) } })),
    onSuccess: (res) => {
      const made = new Map(res.items.map((x) => [x.id, x.barcode]));
      setRows((rs) => rs.map((r) => (made.has(r.id) ? { ...r, barcode: made.get(r.id)! } : r)));
      void message.success(t("bc.generated", { n: res.items.length }));
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const total = rows.reduce((n, r) => n + r.copies, 0);
  return (
    <>
      <Flex gap={12} wrap style={{ marginBottom: 12 }}>
        <div style={{ flex: "1 1 260px" }}>
          <SkuSelect value={undefined} onChange={(id) => void add(id)} placeholder={t("bc.addSku")} />
        </div>
        {missing.length > 0 && can("catalog.manage") && (
          <Button loading={generate.isPending} onClick={() => generate.mutate()}>
            {t("bc.generate", { n: missing.length })}
          </Button>
        )}
        <PrintButton n={total} printing={printing} onClick={() => onPrint(rows.map((r) => ({ id: r.id, copies: r.copies })))} />
      </Flex>
      <Table<SkuRow>
        rowKey="id"
        size="small"
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: t("bc.skuEmpty") }}
        columns={[
          {
            title: t("bc.sku"),
            render: (_, r) => (
              <span>
                <Typography.Text strong className="z-num">
                  {r.code}
                </Typography.Text>{" "}
                · {r.name}
              </span>
            ),
          },
          {
            title: t("bc.barcode"),
            dataIndex: "barcode",
            width: 160,
            render: (v: string | null) => (v ? <span className="z-num">{v}</span> : <Typography.Text type="secondary">{t("bc.noBarcode")}</Typography.Text>),
          },
          {
            title: t("bc.copies"),
            width: 110,
            render: (_, r) => (
              <InputNumber
                size="small"
                min={1}
                max={100}
                precision={0}
                value={r.copies}
                onChange={(v) => setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, copies: v ?? 1 } : x)))}
              />
            ),
          },
          {
            width: 48,
            render: (_, r) => <Button type="text" size="small" icon={<DeleteOutlined />} aria-label={t("common.delete")} onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))} />,
          },
        ]}
      />
    </>
  );
}

/** Free text, one code per line; after a tab or ";" comes the line printed with it. */
function CustomText({ printing, copies, onPrint }: { printing: boolean; copies: number; onPrint: (items: Item[]) => void }) {
  const { t } = useI18n();
  const [text, setText] = useState("");
  const items: Item[] = text
    .split("\n")
    .map((line) => line.split(/\t|;/))
    .filter(([code]) => (code ?? "").trim() !== "")
    .slice(0, 500)
    .map(([code, ...rest]) => ({ text: (code ?? "").trim(), title: rest.join(" ").trim(), copies }));
  return (
    <>
      <Typography.Paragraph type="secondary">{t("bc.customHelp")}</Typography.Paragraph>
      <Input.TextArea rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder={"RAK-PROMO;Rak promo depan\nBOX-RETUR;Kotak retur"} style={{ marginBottom: 12 }} />
      <PrintButton n={items.length * copies} printing={printing} onClick={() => onPrint(items)} />
    </>
  );
}
